import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { OFFICIAL_LOGO, EVENT_TAGLINE } from "@/lib/brand";

export default function IntroAnimation() {
  const [show, setShow] = useState(() => {
    if (typeof window !== "undefined") {
      return !sessionStorage.getItem("amsc_intro_seen");
    }
    return false;
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768;
    }
    return false;
  });
  const prefersReducedMotion = useReducedMotion();

  const dismiss = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("amsc_intro_seen", "1");
    }
    setShow(false);
  }, []);

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => dismiss(), 4500);
    return () => clearTimeout(t);
  }, [show, dismiss]);

  useEffect(() => {
    if (show) document.body.style.overflow = "hidden";
  }, [show]);

  // Escape key to dismiss
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, dismiss]);

  const tagline = EVENT_TAGLINE.split("");
  // Diagonal covers corner-to-corner from center; a small margin (1.3x) is
  // plenty. The previous 2.2x multiplier rasterized an unnecessarily huge
  // transparent layer (~3168px on a 1440px-wide screen) every animation frame.
  const ringSizePx = typeof window !== "undefined"
    ? Math.hypot(window.innerWidth, window.innerHeight) * 1.3
    : 1200;

  // Reduced-motion: skip decorative animations, just show logo + simple fade
  if (prefersReducedMotion) {
    return (
      <AnimatePresence onExitComplete={() => { document.body.style.overflow = ""; }}>
        {show && (
          <motion.div
            key="intro-reduced"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={dismiss}
            role="dialog"
            aria-modal="true"
            aria-label="Welcome to Asian Masters Swimming Championships 2026"
            className="fixed inset-0 z-[9999] flex items-center justify-center cursor-pointer"
            style={{ background: "#0B1429", overflow: "hidden" }}
            data-testid="intro-animation"
          >
            <div className="flex flex-col items-center px-4 text-center">
              <div className="bg-white rounded-3xl p-4 sm:p-8">
                <img
                  src={OFFICIAL_LOGO}
                  alt="2nd Asian Masters Swimming Championships 2026"
                  className="w-40 h-40 sm:w-64 sm:h-64 object-contain"
                />
              </div>
              <p className="mt-6 text-white font-display font-black text-xl sm:text-3xl">{EVENT_TAGLINE}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence onExitComplete={() => { document.body.style.overflow = ""; }}>
      {show && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          onClick={dismiss}
          role="dialog"
          aria-modal="true"
          aria-label="Welcome to Asian Masters Swimming Championships 2026"
          className="fixed inset-0 z-[9999] flex items-center justify-center cursor-pointer"
          style={{
            background: "radial-gradient(ellipse at center, #0B1429 0%, #000000 100%)",
            willChange: "opacity",
            overflow: "hidden",
            overscrollBehavior: "contain",
          }}
          data-testid="intro-animation"
        >
          {/* Rotating light rays — GPU-composited, clipped by parent overflow:hidden */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ willChange: "transform" }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 1200 1200"
              style={{ position: "absolute", inset: 0, opacity: 0.22 }}
              preserveAspectRatio="xMidYMid slice"
              aria-hidden="true"
            >
              <defs>
                <radialGradient id="ray-grad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
                </radialGradient>
              </defs>
              {Array.from({ length: 18 }).map((_, i) => (
                <path
                  key={`ray-${i}`}
                  d="M 600 600 L 600 0 L 640 0 Z"
                  fill="url(#ray-grad)"
                  transform={`rotate(${i * 20} 600 600)`}
                />
              ))}
            </svg>
          </motion.div>

          {/* Caustic orb — lighter blur on mobile. Blur lives on a static inner
              layer so the animated scale/opacity on the outer layer is a pure
              compositor transform, instead of forcing a blur re-rasterize on
              every frame. */}
          <motion.div
            className="absolute rounded-full pointer-events-none"
            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.15, 0.4, 0.15] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden="true"
            style={{
              width: isMobile ? "90vw" : "700px",
              height: isMobile ? "90vw" : "700px",
              willChange: "transform, opacity",
            }}
          >
            <div
              className="w-full h-full rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(0,240,255,0.5) 0%, transparent 60%)",
                filter: isMobile ? "blur(24px)" : "blur(60px)",
              }}
            />
          </motion.div>

          {/* Ripple rings — 2 on mobile (not 3) to reduce GPU load */}
          {(isMobile ? [0, 0.9] : [0, 0.6, 1.2]).map((delay) => (
            <motion.span
              key={`ripple-${delay}`}
              className="absolute rounded-full border border-[#00F0FF]/30 pointer-events-none"
              aria-hidden="true"
              style={{
                width: ringSizePx,
                height: ringSizePx,
                willChange: "transform, opacity",
              }}
              initial={{ scale: 0.1, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 0 }}
              transition={{ duration: 2.5, delay, repeat: Infinity, ease: "easeOut" }}
            />
          ))}

          {/* Main logo with bounce-in */}
          <motion.div
            initial={{ scale: 0, opacity: 0, rotate: -30 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 14, delay: 0.3 }}
            className="relative flex flex-col items-center z-10 px-4 w-full"
          >
            <motion.div
              className="bg-white rounded-3xl p-4 sm:p-6 md:p-8 relative"
              animate={{
                boxShadow: [
                  "0 0 40px rgba(0,240,255,0.4)",
                  "0 0 90px rgba(0,240,255,0.75)",
                  "0 0 40px rgba(0,240,255,0.4)",
                ],
              }}
              transition={{ duration: 2.5, repeat: Infinity }}
              style={{ willChange: "box-shadow" }}
            >
              <img
                src={OFFICIAL_LOGO}
                alt="2nd Asian Masters Swimming Championships 2026"
                className="w-36 h-36 sm:w-56 sm:h-56 md:w-72 md:h-72 object-contain"
              />
            </motion.div>

            {/* Tagline letter-by-letter */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="mt-6 sm:mt-8 font-display font-black text-base sm:text-2xl md:text-4xl flex justify-center w-full px-2 max-w-4xl mx-auto text-center"
              aria-label={EVENT_TAGLINE}
            >
              {tagline.map((ch, i) => (
                <motion.span
                  key={`tagline-${i}-${ch}`}
                  initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ delay: 1.7 + i * 0.025, duration: 0.4, ease: "easeOut" }}
                  className="inline-block text-white"
                  aria-hidden="true"
                >
                  {ch === " " ? " " : ch}
                </motion.span>
              ))}
            </motion.div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
